import React from 'react';
import {
	CheckOutlined,
	DeleteOutlined,
	ReloadOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import {
	Select,
	Space,
	Tag,
	Typography,
} from 'antd';
import { Moment } from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faFileInvoice,
	faUndo,
} from '@fortawesome/pro-light-svg-icons';
import {
	Icon,
	IconProp,
} from '@fortawesome/fontawesome-svg-core';
import {
	AccountingItemApprovalStates,
	AccountTypes,
	Currencies,
	CurrencySymbols,
	DATE_AND_TIME,
	FixtureTypeLabels,
	FixtureTypes,
	FuelTypes,
	HireInvoiceItemStates,
	TcInAllocationItemTypes,
	TimeZoneFormat,
} from '@shared/utils/constants';
import { getPayerString } from '@shared/utils/string';
import { formatCurrency } from '@shared/utils/currency';
import {
	formatDuration,
	toMoment,
} from '@shared/utils/date';
import { standardSort } from '@shared/utils/standardSort';
import { Values } from '@shared/utils/objectEnums';
import HireInvoiceItem from '@shared/hireInvoice/HireInvoiceItem';
import { calculateTotal } from '@shared/utils/math';
import type { BunkerType } from '@api/models/bunker';
import type { FixtureCounterpartyProps } from '@api/models/fixture-counterparty';
import { TcTermsWithIncludes } from '@api/utils/getTcTerms';
import type TcInAllocationItem from '@api/models/tc-in-allocation-item';
import { GetVoyagesResponse } from '@api/features/voyages/getVoyages';
import { formatDate } from '@client/utils/formatDate';
import { Links } from '@client/utils/links';
import {
	getFilterProps,
	renderDate,
} from '@client/utils/table';
import Button from '@client/components/Button';
import StateTag from '@client/components/StateTag';
import MultiCurrencyAmount, { MultiCurrencyValuesType } from '@client/components/MultiCurrencyAmount';
import EmptyText from '@client/components/EmptyText';
import { updateTcInAllocationItem } from '@client/lib/api';
import styles from './styles/tableColumns.module.css';

type CurrenciesType = Values<typeof Currencies>;

// Minimal types to avoid expensive type inference from API response types
type HirePeriodData = {
	from: Moment | Date;
	to: Moment | Date;
	useLegacyTime?: boolean;
};

type HireInvoiceItemRow = {
	HIIHirePeriod?: HirePeriodData;
	HIIBbItem?: HirePeriodData;
};

type HireInvoiceRow = {
	id: number;
	bankAccountId: number | null;
	currency: CurrenciesType;
	amount: number;
	deletedAt?: string | Date | null;
	VoyagePaymentParts: Array<{ amount: number }>;
	HireInvoiceItems: HireInvoiceItemRow[];
	hasAmendments: boolean;
};

type VoyageInvoiceRow = {
	id: number;
	state: AccountingItemApprovalStates;
	accountingSystemId: string | null;
	requiresApproval: boolean;
	posted: boolean;
	deletedAt?: string | Date | null;
	hasAmendments: boolean;
};

type InvoiceActionRow = VoyageInvoiceRow & Partial<Pick<HireInvoiceRow, 'HireInvoiceItems'>>;

type BankAccountSlice = {
	id: number;
	currency: CurrenciesType;
};

type VoyageDetailsSlice = {
	id: number;
	allBankAccounts: BankAccountSlice[];
	hireInvoices: HireInvoiceRow[];
	tcTerms: TcTermsWithIncludes;
};

type ExpenseReceivableRow = {
	currency: CurrenciesType;
	exchangeRate: number;
};

type FixtureDetailsSlice = {
	useUTC: boolean;
	type: Values<typeof FixtureTypes>;
};

const { UTC, LocalTime } = TimeZoneFormat;

const getConfirmTitle = (row: VoyageInvoiceRow) => {
	if (row.hasAmendments) {
		return `This invoice has amendments. 
		Deleting this invoice will also delete the amendments and the reversal`;
	}

	if (row.posted) {
		return `This invoice has already been posted. 
		Deleting this invoice will also delete/reverse the invoice in the accounting system`;
	}

	if (row.state !== AccountingItemApprovalStates.PENDING) {
		return `This invoice has been submitted for approvel or is approved, 
		are you sure you want to delete it?`;
	}

	return 'Are you sure you want to delete this invoice?';
};

const getSharedBunkerColumns = (
	fixtureCurrency: CurrenciesType,
) => [
	{
		dataIndex: 'fuelGrade',
		title: 'Grade',
		editable: true,
		editingProps: {
			type: 'select',
			options: Object.values(FuelTypes).map((ft) => ({ label: ft, value: ft })),
		},
	},
	{
		dataIndex: 'quantity',
		title: 'Quantity',
		render: (text: string) => `${text} MT`,
		editable: true,
		editingProps: {
			type: 'number',
			placeholder: 'MT',
		},
	},
	{
		dataIndex: 'pricePerTon',
		title: 'Price',
		editable: true,
		editingProps: {
			inputProps: {
				baseCurrency: fixtureCurrency,
			},
			type: 'multiCurrency',
			placeholder: `${CurrencySymbols[fixtureCurrency as keyof typeof CurrencySymbols] || fixtureCurrency} / MT`,
		},
		render: (c: MultiCurrencyValuesType) => (c != null ? (
			<MultiCurrencyAmount
				amount={c.value}
				inputCurrency={c.currency}
				fixtureCurrency={fixtureCurrency}
				exchangeRate={c.exchangeRate}
			/>
		) : (<EmptyText />)),
	},
	{
		dataIndex: 'total',
		title: 'Total',
		render: (
			_total: number,
			row: BunkerType & { pricePerTon: MultiCurrencyValuesType },
			_index: number,
			editingRow: number,
		) => {
			let price;

			if (
				row?.pricePerTon?.value != null &&
				row?.pricePerTon?.currency != null &&
				row?.pricePerTon?.exchangeRate != null
			) {
				const { value, currency, exchangeRate } = row.pricePerTon;
				price = (currency !== fixtureCurrency) ?
					value / exchangeRate :
					value;
			}

			return ((
				editingRow == null &&
				price != null
			) ?
				formatCurrency(row.quantity * price, fixtureCurrency) :
				''
			);
		},
	},
];

export const getOffHireBunkerColumns = (
	fixtureCurrency: CurrenciesType,
) => [
	...getSharedBunkerColumns(fixtureCurrency),
];

export const getInvoiceIdentifierColumn = (voyageDetails: { id: number }) => ({
	dataIndex: 'invoiceNumber',
	title: 'Invoice',
	render: (value: string, freightInvoice: { id: number }) => (
		<Link to={Links.VoyageInvoice.get(voyageDetails.id, freightInvoice.id)}>
			<Space>
				<FontAwesomeIcon icon={faFileInvoice as IconProp} />
				{value}
			</Space>
		</Link>
	),
	sorter: () => 1,
});

const getCurrency = (
	invoice: HireInvoiceRow,
	allBankAccounts: BankAccountSlice[]
) => allBankAccounts.find((b) => b.id === invoice.bankAccountId)?.currency ?? invoice.currency ?? 'USD';

const getAmountInvoicedColumn = (
	voyageDetails: VoyageDetailsSlice,
) => ({
	title: 'Invoiced',
	align: 'right',
	dataIndex: 'amount',
	render: (amount: number, invoice: any) => (
		<div>
			{formatCurrency(amount, getCurrency(invoice, voyageDetails.allBankAccounts),
				{ forceDecimals: true })}
		</div>
	),
});

const getSharedInvoiceColumns = ({
	onUndo,
	onDelete,
	onSubmit,
	voyageInvoices,
	voyageDetails,
	isTc,
	fixtureType,
}: {
	voyageInvoices: HireInvoiceRow[];
	voyageDetails: VoyageDetailsSlice;
	onDelete: (id: number) => void;
	onSubmit: (id: number) => void;
	onUndo: (id: number) => void;
	isTc: boolean;
	fixtureType?: Values<typeof FixtureTypes>;
}) => {
	const getPaidAmount = (
		item: HireInvoiceRow
	) => calculateTotal(item.VoyagePaymentParts, (payment) => payment.amount);

	return [
		{
			dataIndex: '',
			title: 'Paid',
			align: 'right',
			render: (
				item: HireInvoiceRow
			) => formatCurrency(getPaidAmount(item), getCurrency(item, voyageDetails.allBankAccounts)),
		},
		{
			dataIndex: '',
			title: 'Outstanding',
			align: 'right',
			render: (item: HireInvoiceRow) => {
				const paid = getPaidAmount(item);

				const outstanding = item.amount - paid;

				return formatCurrency(outstanding, getCurrency(item, voyageDetails.allBankAccounts));
			},
		},
		{
			dataIndex: 'state',
			title: 'Status',
			render: (
				state: AccountingItemApprovalStates,
				row: VoyageInvoiceRow,
			) => row.deletedAt != null ?
				(<Tag color="red">Deleted</Tag>) :
				(<StateTag state={state} />),
		},
		{
			dataIndex: '',
			title: '',
			align: 'center' as const,
			render: (_v: any, row: InvoiceActionRow) => {
				if (row.deletedAt != null) {
					return null;
				}

				const canBeSubmitted = row.state === AccountingItemApprovalStates.PENDING;
				const latestActiveInvoice = voyageInvoices.filter((i) => i.deletedAt == null)[0];
				const tcLatestInvoiceRuleApplies = isTc && (
					fixtureType == null ||
					hireInvoiceHasPeriodOnInvoice(row, fixtureType)
				);
				const blockedByTcLatestOnly = (
					tcLatestInvoiceRuleApplies &&
					latestActiveInvoice?.id !== row.id
				);
				const deleteDisabled = blockedByTcLatestOnly || row.accountingSystemId != null;
				const deleteMessage = row.accountingSystemId != null ? 
					'This invoice has been posted to accounts' : 
					'You can only delete the latest invoice';
				const { requiresApproval } = row;

				return (
					<div>
						{canBeSubmitted && (
							<Button
								onClick={() => onSubmit(row.id)}
								type="link"
								icon={(<CheckOutlined />)}
							>
								{`${requiresApproval ? 'Submit for approval' : 'Approve'}`}
							</Button>
						)}
						{(requiresApproval && !canBeSubmitted) && (
							<Button
								icon={(
									<FontAwesomeIcon
										icon={faUndo as Icon}
										className={styles.undoIcon}
									/>
								)}
								type="link"
								className={styles.undoButton}
								disabled={row.state === AccountingItemApprovalStates.REJECTED}
								disabledTooltip={`You cannot unsubmit a rejected item. 
								If you wish to make changes, delete the item and re-submit.`}
								onClick={async () => {
									await onUndo(row.id);
								}}
							>
								Undo submission
							</Button>
						)}
						{row.accountingSystemId == null ? (
							<Button
								disabled={deleteDisabled}
								confirmTitle={getConfirmTitle(row)}
								className={styles.dismissButton}
								onClick={() => onDelete(row.id)}
								type="link"
								icon={(<DeleteOutlined />)}
								danger
								disabledTooltip={deleteMessage}
							/>
						) : (
							<Button
								disabled={blockedByTcLatestOnly}
								confirmTitle={row.hasAmendments ? getConfirmTitle(row) : `This invoice has been posted to accounts. 
								Would you like to reverse it? This will create a credit note in your accounting system, 
								and allow you to create a new invoice in ClearVoyage.`}
								type="link"
								icon={(<ReloadOutlined />)}
								danger
								onClick={() => onDelete(row.id)}
							>
								Reverse
							</Button>
						)}
					</div>
				);
			},
		},
	];
};

export const getFreightInvoiceColumns = ({
	voyageDetails,
	charterers,
	onDelete,
	onSubmit,
	onUndo,
}: {
	charterers: FixtureCounterpartyProps[] | undefined;
	voyageDetails: VoyageDetailsSlice;
	voyageInvoices: VoyageInvoiceRow[];
	onDelete: (id: number) => void;
	onSubmit: (id: number) => void;
	onUndo: (id: number) => void;
}) => [
	getInvoiceIdentifierColumn(voyageDetails),
	{
		title: 'Charterer',
		dataIndex: 'chartererId',
		render: (chartererId: number) => (charterers != null ? charterers.find((c) => c.id === chartererId)?.name : ''),
	},
	{
		title: 'Items',
		dataIndex: 'items',
		render: (items: Array<HireInvoiceItem>) => {
			const labels = items.map((i) => i.itemTypeLabel);

			return labels.join(', ');
		},
	},
	getAmountInvoicedColumn(voyageDetails),
	...getSharedInvoiceColumns({
		onUndo,
		onDelete,
		onSubmit,
		voyageInvoices: voyageDetails.hireInvoices ?? [],
		voyageDetails,
		isTc: false,
	}),

];

const getPeriodColumnTitle = (fixtureDetails: FixtureDetailsSlice) => {
	const { useUTC } = fixtureDetails;
	const fixtureType = fixtureDetails.type;

	let string = 'Period';

	if (fixtureType !== FixtureTypes.BB_OUT) {
		string = `Hire ${string}`;
	}

	if (useUTC) {
		string += ' (UTC)';
	}

	return string;
};

/** Matches the Hire Period column: false when that column shows an em dash (no period). */
const hireInvoiceHasPeriodOnInvoice = (
	row: { HireInvoiceItems?: HireInvoiceItemRow[] },
	fixtureType: Values<typeof FixtureTypes>,
): boolean => {
	const hireInvoiceItems = row.HireInvoiceItems ?? [];
	const key: 'HIIHirePeriod' | 'HIIBbItem' = fixtureType === FixtureTypes.BB_OUT ?
		'HIIBbItem' :
		'HIIHirePeriod';

	if (hireInvoiceItems.length === 0) {
		return false;
	}

	const firstItem = hireInvoiceItems[0];
	const nextItem = hireInvoiceItems[1];

	if (firstItem?.[key] == null) {
		return false;
	}

	const firstPeriod = firstItem[key]!;
	const nextPeriod = nextItem?.[key];

	let toDate = firstPeriod?.to;

	if (nextPeriod != null) {
		toDate = nextPeriod.to;
	}

	if (toDate == null || firstPeriod?.from == null) {
		return false;
	}

	return true;
};

export const getHireInvoiceColumns = ({
	voyageDetails,
	fixtureDetails,
	onDelete,
	onSubmit,
	onUndo,
}: {
	voyageDetails: VoyageDetailsSlice;
	fixtureDetails: FixtureDetailsSlice;
	onDelete: (id: number) => void;
	onSubmit: (id: number) => void;
	onUndo: (id: number) => void;
}) => [
	getInvoiceIdentifierColumn(voyageDetails),
	{
		title: getPeriodColumnTitle(fixtureDetails),
		render: (_: any, row: HireInvoiceRow) => {
			const hireInvoiceItems = row.HireInvoiceItems;
			let key: 'HIIHirePeriod' | 'HIIBbItem' = 'HIIHirePeriod';

			if (fixtureDetails.type === FixtureTypes.BB_OUT) {
				key = 'HIIBbItem';
			}

			if (hireInvoiceItems.length === 0) {
				return '—';
			}

			const firstItem = hireInvoiceItems[0];
			const nextItem = hireInvoiceItems?.[1];

			if (firstItem?.[key] == null) {
				return '—';
			}

			const firstPeriod = firstItem[key]!;
			const nextPeriod = nextItem?.[key];

			let toDate = firstPeriod?.to;

			if (nextPeriod != null) {
				toDate = nextPeriod.to;
			}

			if (toDate == null || firstPeriod?.from == null) {
				return '—';
			}

			const from = voyageDetails.tcTerms.useUTC ?
				toMoment(firstPeriod.from).clone().utc() :
				toMoment(firstPeriod.from).clone();

			const to = voyageDetails.tcTerms.useUTC ?
				toMoment(firstPeriod.to).clone().utc() :
				toMoment(firstPeriod.to).clone();

			const timeFormat = voyageDetails.tcTerms.useUTC ? UTC : LocalTime;

			let formattedFromDate = formatDate(from, DATE_AND_TIME, true, timeFormat);
			let formattedToDate = formatDate(to, DATE_AND_TIME, true, timeFormat);

			if (
				firstPeriod.useLegacyTime && 
				voyageDetails.tcTerms.useUTC && 
				formattedFromDate != null && 
				formattedToDate != null
			) {
				formattedFromDate = formattedFromDate.replace('UTC', 'LT');
				formattedToDate = formattedToDate.replace('UTC', 'LT');
			}

			return (
				<>
					{formattedFromDate}
					<b> - </b>
					{formattedToDate}
				</>
			);
		},
	},
	{
		title: 'Type',
		render: (row: { invoiceTitle: string }) => row?.invoiceTitle,
	},
	getAmountInvoicedColumn(voyageDetails),
	...getSharedInvoiceColumns({
		onUndo,
		onDelete,
		onSubmit,
		voyageInvoices: voyageDetails.hireInvoices ?? [],
		voyageDetails,
		isTc: true,
		fixtureType: fixtureDetails.type,
	}),
];

export const getExpenseColumns = (
	fixtureCurrency: CurrenciesType,
	fixtureType: Values<typeof FixtureTypes>,
	hasDifferences: boolean,
	linkedOutVoyages: GetVoyagesResponse['rows'],
	tcInAllocationItems: TcInAllocationItem[],
	refreshDetails: () => void,
) => [
	{
		dataIndex: 'createdAt',
		title: 'Creation date',
		render: renderDate(),
		sorter: standardSort('createdAt'),
	},
	{
		dataIndex: 'account',
		title: 'Account',
		render: (account: Values<typeof AccountTypes>) => getPayerString(account, fixtureType),
		...getFilterProps(Object.values(AccountTypes), 'select', 'account', 'asc', true),
		sorter: standardSort('account'),
	},
	{
		dataIndex: 'itemDescription',
		title: 'Item',
	},
	...(hasDifferences ?
		[{
			dataIndex: 'amount',
			title: 'Local Amount',
			align: 'right',
			render: (amount: number, row: ExpenseReceivableRow) => (
				formatCurrency(amount, row.currency)
			),
		},
		{
			dataIndex: 'amount',
			title: 'Contract Amount',
			align: 'right',
			render: (amount: number, row: ExpenseReceivableRow) => (
				formatCurrency(amount / row.exchangeRate, fixtureCurrency)
			),
		}] : [{
			dataIndex: 'amount',
			title: `Amount (${fixtureCurrency}) `,
			align: 'right',
			render: (amount: number, row: ExpenseReceivableRow) => (
				formatCurrency(amount / row.exchangeRate, fixtureCurrency, { hideSymbol: true })
			),
		}]
	),
	...(fixtureType === FixtureTypes.TC_IN ? [{
		title: 'Allocate to voyage',
		dataIndex: 'allocation',
		width: 220,
		render: (_value: unknown, row: { id: number }) => {
			const allocation = tcInAllocationItems.find((item) => (
				item.itemType === TcInAllocationItemTypes.VOYAGE_EXPENSE &&
			item.itemId === row.id
			));

			const selectedVoyageId = allocation?.allocatedToId ?? undefined;

			return (
				<Select
					allowClear
					placeholder="Select voyage"
					value={selectedVoyageId}
					style={{ width: '100%' }}
					status={selectedVoyageId == null ? 'error' : undefined}
					options={linkedOutVoyages.map((voyage) => ({
						value: voyage.id,
						label: `${voyage.identifier} - ${FixtureTypeLabels[voyage.fixtureType]}`,
					}))}
					onChange={async (newVoyageId?: number) => {
						if (newVoyageId != null) {
							await updateTcInAllocationItem(
								newVoyageId,
								row.id,
								TcInAllocationItemTypes.VOYAGE_EXPENSE,
								100,
							);
						} else if (selectedVoyageId != null) {
							await updateTcInAllocationItem(
								selectedVoyageId,
								row.id,
								TcInAllocationItemTypes.VOYAGE_EXPENSE,
								0,
							);
						}

						await refreshDetails();
					}}
				/>
			);
		},
	}] : []),
	{
		dataIndex: 'state',
		title: 'State',
		render: (state: Values<typeof AccountingItemApprovalStates>) => (
			<StateTag state={state} />
		),
		width: 100,
		sorter: standardSort('state'),
		...getFilterProps([
			HireInvoiceItemStates.PENDING,
			HireInvoiceItemStates.INVOICED,
			HireInvoiceItemStates.UNRESOLVED,
			HireInvoiceItemStates.PAID,
			HireInvoiceItemStates.OVERPAID,
			HireInvoiceItemStates.PARTIAL,
		], 'select', 'state', undefined, true),
	},
];

export const getOffHireColumns = (
	useUTC: boolean,
	orgHasOffhireTypes: boolean,
	fixtureCurrency: CurrenciesType,
) => [
	{
		dataIndex: 'startTime',
		title: 'Start time',
		render: (
			date: Moment,
			{ selectedTimeFormat }: { selectedTimeFormat: TimeZoneFormat },
		) => formatDate(date, DATE_AND_TIME, true, selectedTimeFormat),
		sorter: standardSort('startTime'),
	},
	{
		dataIndex: 'endTime',
		title: 'End time',
		render: (
			date: Moment,
			{ selectedTimeFormat }: { selectedTimeFormat: TimeZoneFormat },
		) => formatDate(date, DATE_AND_TIME, true, selectedTimeFormat),
		sorter: standardSort('endTime'),
	},
	{
		dataIndex: 'duration',
		title: 'Duration',
		render: (
			_d: any,
			{ startTime, endTime }: { startTime: Moment; endTime: Moment },
			_index: number,
			editingRow: number,
		) => {
			if (editingRow != null || startTime == null || endTime == null) {
				return '';
			}

			return formatDuration({ rawStart: startTime, rawEnd: endTime });
		},
	},
	{
		dataIndex: 'note',
		title: 'Note',
		width: 200,
		render: (note: string) => (
			<Typography.Text
				ellipsis
				style={{ width: 300 }}
			>
				{note}
			</Typography.Text>
		),
	},
	(orgHasOffhireTypes ? {
		dataIndex: 'type',
		title: 'Type',
	} : []),
	{
		title: `Amount (${fixtureCurrency})`,
		dataIndex: 'amount',
		align: 'right',
		render: (amount: number) => formatCurrency(amount, fixtureCurrency, { hideSymbol: true }),
	},
	{
		dataIndex: 'state',
		title: 'State',
		render: (state: Values<typeof AccountingItemApprovalStates>) => (
			<StateTag state={state} />
		),
		sorter: standardSort('state'),
	},
];
